import { z } from "zod";

/**
 * Paid Media Overview v2 — Zod schemas.
 *
 * Source of truth: backend `specs/003-brand-intelligence-api/contracts/paid-media-overview.md`.
 * Schemas are intentionally permissive on enums (passthrough on objects, fall
 * back to `z.string()` on category-style fields) so a backend-side enum
 * extension doesn't crash the page mid-deploy. The UI keys off `platforms_included`
 * for which platform groups to render — never assume both are present.
 */

// ---------- shared building blocks ----------

export const PlatformSchema = z.enum(["google", "meta"]);
export type OverviewV2Platform = z.infer<typeof PlatformSchema>;

/**
 * Tab-level platform identifier used inside `snapshot.platforms[].platform`.
 *
 * Permissive `z.string()` (NOT an enum) because:
 *   - The backend already emits a synthetic `"combined"` value when ≥2 real
 *     platforms returned data.
 *   - More platforms will land later (TikTok, Pinterest, etc.) and a strict
 *     enum here would crash the entire page on the first unknown value.
 *
 * The TS type stays narrow (`OverviewV2SnapshotPlatformValue`) so call sites
 * still get autocomplete for the three known values; any new value falls
 * through as the literal string and gets a sensible default rendering
 * (brand-icon-less tab, label = the raw string).
 *
 * `platforms_included` stays strict at `"google" | "meta"` so it remains a
 * true "data sources" badge — never includes synthetic groups.
 */
export const SnapshotPlatformValueSchema = z.string();
export type OverviewV2SnapshotPlatformValue = "google" | "meta" | "combined" | (string & {});

export const DirectionSchema = z.enum(["up", "down", "stable"]);
export type OverviewV2Direction = z.infer<typeof DirectionSchema>;

/**
 * Envelope shared by every endpoint. The combined `/` endpoint inlines these
 * top-level (no `cached` field) — see `SummaryResponseSchema` below.
 */
export const EnvelopeSchema = z.object({
  customer_id: z.string(),
  synced_at: z.string(),
  cached: z.boolean(),
  currency: z.string(),
});
export type OverviewV2Envelope = z.infer<typeof EnvelopeSchema>;

// ---------- /snapshot ----------

export const KpiKeySchema = z.enum(["roas", "cpa", "spend", "conversions"]);
export type OverviewV2KpiKey = z.infer<typeof KpiKeySchema>;

const KpiBucketSchema = z.object({
  value: z.number().nullable(),
  delta_pct: z.number().nullable(),
  direction: DirectionSchema,
});
export type OverviewV2KpiBucket = z.infer<typeof KpiBucketSchema>;

const KpiSchema = z.object({
  key: KpiKeySchema,
  label: z.string(),
  yesterday: KpiBucketSchema,
  mtd: KpiBucketSchema,
  prev_month: KpiBucketSchema,
  /** Length 7, oldest → newest. Nulls = no data that day; do NOT interpolate. */
  sparkline: z.array(z.number().nullable()),
});
export type OverviewV2Kpi = z.infer<typeof KpiSchema>;

const SnapshotPlatformSchema = z.object({
  platform: SnapshotPlatformValueSchema,
  kpis: z.array(KpiSchema),
});
export type OverviewV2SnapshotPlatform = z.infer<typeof SnapshotPlatformSchema>;

export const SnapshotResponseSchema = EnvelopeSchema.extend({
  platforms_included: z.array(PlatformSchema),
  platforms: z.array(SnapshotPlatformSchema),
});
export type OverviewV2SnapshotResponse = z.infer<typeof SnapshotResponseSchema>;

// ---------- /top-movers ----------

export const MoverMetricSchema = z.enum([
  "roas",
  "cpa",
  "spend",
  "ctr",
  "cpc",
  "conv_rate",
]);
export type OverviewV2MoverMetric = z.infer<typeof MoverMetricSchema>;

/**
 * Mover entity type. Permissive `z.string()` (NOT enum) for the same reason
 * as `SnapshotPlatformValueSchema`: the backend already added `"slice"` on
 * top of `"account" | "campaign"`, and more types (ad_group, asset, etc.)
 * will land later. A strict enum would crash the page on the next addition.
 *
 * The narrow TS type still autocompletes the three known values via the
 * `(string & {})` trick.
 */
export const MoverEntityTypeSchema = z.string();
export type OverviewV2MoverEntityType =
  | "account"
  | "campaign"
  | "slice"
  | (string & {});

/**
 * Categorical key for slice movers (only set when `entity_type === "slice"`).
 * Open-ended on the wire — `entity_name` is the display string, this field
 * is the dimension grouping. Treated as `z.string()` so a future
 * `"device_type"` etc. doesn't break parsing.
 */
export const MoverSliceDimensionSchema = z.string().nullable().optional();
export type OverviewV2MoverSliceDimension =
  | "channel_type" // Google
  | "publisher_platform" // Meta
  | "platform_position" // Meta
  | (string & {});

const MoverSchema = z.object({
  platform: PlatformSchema,
  metric: MoverMetricSchema,
  entity_type: MoverEntityTypeSchema,
  entity_id: z.string(),
  entity_name: z.string(),
  delta_pct: z.number(),
  direction: z.enum(["up", "down"]),
  annotation: z.string(),
  baseline_window: z.string(),
  current_window: z.string(),
  /**
   * Only set when `entity_type === "slice"` — null/absent for account /
   * campaign movers. Drives the optional sub-grouping ("Channels",
   * "Publishers", "Placements") if/when we want it.
   */
  slice_dimension: MoverSliceDimensionSchema,
});
export type OverviewV2Mover = z.infer<typeof MoverSchema>;

export const TopMoversResponseSchema = EnvelopeSchema.extend({
  movers: z.array(MoverSchema),
});
export type OverviewV2TopMoversResponse = z.infer<typeof TopMoversResponseSchema>;

// ---------- /external-signals ----------

export const ExternalSignalProviderSchema = z.enum(["curated", "web_search"]);
export type OverviewV2ExternalSignalProvider = z.infer<typeof ExternalSignalProviderSchema>;

export const ExternalSignalCategorySchema = z.string();
export const KNOWN_EXTERNAL_SIGNAL_CATEGORIES = [
  "seasonality",
  "platform_update",
  "competitor",
  "regulatory",
  "macro",
] as const;
export type OverviewV2KnownExternalSignalCategory =
  (typeof KNOWN_EXTERNAL_SIGNAL_CATEGORIES)[number];

const ExternalSignalSchema = z.object({
  label: z.string(),
  category: ExternalSignalCategorySchema,
  /** 0 = active now; >0 = upcoming. */
  lead_time_days: z.number(),
  source: ExternalSignalProviderSchema,
  evidence_url: z.string().nullable(),
});
export type OverviewV2ExternalSignal = z.infer<typeof ExternalSignalSchema>;

export const ExternalSignalsResponseSchema = EnvelopeSchema.extend({
  provider: ExternalSignalProviderSchema,
  signals: z.array(ExternalSignalSchema),
});
export type OverviewV2ExternalSignalsResponse = z.infer<typeof ExternalSignalsResponseSchema>;

// ---------- combined / (recommended for page load) ----------

const SectionErrorSchema = z.object({
  section: z.string(),
  error_code: z.string(),
  details: z.string(),
});
export type OverviewV2SectionError = z.infer<typeof SectionErrorSchema>;

/**
 * The combined `/` payload. Note the envelope is inlined (no `cached` field)
 * and each section is individually nullable — the page MUST render each
 * section independently and surface a per-section retry when its key is
 * `null` (matched with the corresponding entry in `errors`).
 */
export const SummaryResponseSchema = z.object({
  customer_id: z.string(),
  synced_at: z.string(),
  currency: z.string(),
  platforms_included: z.array(PlatformSchema),
  snapshot: SnapshotResponseSchema.nullable(),
  top_movers: TopMoversResponseSchema.nullable(),
  external_signals: ExternalSignalsResponseSchema.nullable(),
  errors: z.array(SectionErrorSchema),
});
export type OverviewV2SummaryResponse = z.infer<typeof SummaryResponseSchema>;

// ---------- error envelope ----------

/**
 * Non-200 error body. Backend always sets `error_code`; we branch on
 * `NO_PLATFORM_AVAILABLE` (full-page empty state) vs everything else
 * (per-section retry / generic toast).
 */
export const OverviewV2ErrorBodySchema = z.object({
  error: z.string(),
  error_code: z.string(),
  details: z.string().optional(),
  account_id: z.string().optional(),
  timestamp: z.string().optional(),
});
export type OverviewV2ErrorBody = z.infer<typeof OverviewV2ErrorBodySchema>;
